import { h } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { route } from 'preact-router';
import { api } from '../../state/auth.js';
import { currentPath } from '../../state/ui.js';

// Maps route paths to page_slug values used in HelpArticles
function getPageSlug(path) {
  if (path.startsWith('/dashboard')) return 'dashboard';
  if (path.startsWith('/processes')) return 'processes';
  if (path.startsWith('/tasks')) return 'tasks';
  if (path.startsWith('/documents')) return 'documents';
  if (path.startsWith('/policies')) return 'policies';
  if (path.startsWith('/templates')) return 'templates';
  if (path.startsWith('/users')) return 'users';
  if (path.startsWith('/notifications')) return 'notifications';
  if (path.startsWith('/audit')) return 'audit';
  if (path.startsWith('/reports')) return 'reports';
  if (path.startsWith('/org-chart')) return 'org-chart';
  if (path.startsWith('/settings')) return 'settings';
  if (path.startsWith('/roles')) return 'roles';
  if (path.startsWith('/sections')) return 'sections';
  if (path.startsWith('/seasons')) return 'seasons';
  if (path.startsWith('/recurring')) return 'recurring';
  if (path.startsWith('/coins')) return 'coins';
  if (path.startsWith('/calendar')) return 'calendar';
  if (path.startsWith('/jaarplanning')) return 'jaarplanning';
  if (path.startsWith('/marketplace')) return 'marketplace';
  if (path.startsWith('/sportlink')) return 'sportlink';
  if (path.startsWith('/news')) return 'news';
  if (path.startsWith('/profile')) return 'profile';
  if (path.startsWith('/help')) return 'help';
  if (path.startsWith('/meetings')) return 'meetings';
  if (path.startsWith('/decisions')) return 'decisions';
  if (path.startsWith('/vrijwilligers')) return 'vrijwilligers';
  if (path.startsWith('/storingen')) return 'storingen';
  if (path.startsWith('/kwalificaties')) return 'kwalificaties';
  if (path.startsWith('/goals')) return 'goals';
  if (path.startsWith('/clubtrots')) return 'clubtrots';
  if (path.startsWith('/inrichten')) return 'inrichten';
  return null;
}

const TYPE_COLORS = {
  handleiding:    { bg: '#dbeafe', color: '#1d4ed8' },
  werkinstructie: { bg: '#dcfce7', color: '#16a34a' },
};

function renderMarkdown(text) {
  if (!text) return null;
  return text.split('\n').map((line, i) => {
    if (line.startsWith('## ')) return <div key={i} style="font-size:13px;font-weight:700;margin:12px 0 4px;border-bottom:1px solid var(--color-border);padding-bottom:3px">{line.slice(3)}</div>;
    if (line.startsWith('### ')) return <div key={i} style="font-size:12px;font-weight:600;margin:8px 0 3px">{line.slice(4)}</div>;
    if (line.match(/^- /)) return <div key={i} style="font-size:12px;padding-left:12px;line-height:1.6;color:var(--color-text)">• {line.slice(2).replace(/\*\*(.+?)\*\*/g, '$1')}</div>;
    if (line.match(/^\d+\. /)) return <div key={i} style="font-size:12px;padding-left:12px;line-height:1.6;color:var(--color-text)">{line.replace(/\*\*(.+?)\*\*/g, '$1')}</div>;
    if (!line.trim()) return <div key={i} style="height:6px" />;
    return <p key={i} style="font-size:12px;line-height:1.65;margin:0 0 4px;color:var(--color-text)" dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>').replace(/\*(.+?)\*/g, '<em>$1</em>') }} />;
  });
}

export default function HelpButton() {
  const [open, setOpen] = useState(false);
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(false);

  // Use the shared signal so path updates instantly on Preact Router navigation
  const path = currentPath.value;

  // When panel opens or path changes: load the article for the current page
  useEffect(() => {
    if (!open) { setArticle(null); return; }
    const slug = getPageSlug(path);
    if (!slug) return;
    setLoading(true);
    api(`/api/help/articles?page=${slug}&strict=1`)
      .then(data => {
        const arts = Array.isArray(data) ? data : [];
        if (arts.length > 0) {
          return api(`/api/help/articles/${arts[0].id}`);
        }
        return null;
      })
      .then(art => setArticle(art || null))
      .catch(() => setArticle(null))
      .finally(() => setLoading(false));
  }, [open, path]);

  const slug = getPageSlug(path);

  return (
    <>
      {/* Floating button */}
      <button
        onClick={() => setOpen(o => !o)}
        style={`
          position:fixed;bottom:24px;right:24px;z-index:1000;
          width:44px;height:44px;border-radius:50%;
          background:${open ? 'var(--color-primary)' : 'var(--color-surface)'};
          border:2px solid var(--color-primary);
          color:${open ? '#fff' : 'var(--color-primary)'};
          font-size:18px;font-weight:700;
          cursor:pointer;
          box-shadow:0 4px 16px rgba(0,0,0,0.15);
          transition:all .2s;
          display:flex;align-items:center;justify-content:center;
        `}
        title="Hulp & handleiding"
      >
        {open ? '×' : '?'}
      </button>

      {/* Help panel */}
      {open && (
        <div style="
          position:fixed;bottom:80px;right:24px;z-index:999;
          width:360px;max-height:78vh;
          background:var(--color-surface);
          border:1px solid var(--color-border);
          border-radius:16px;
          box-shadow:0 8px 40px rgba(0,0,0,0.18);
          display:flex;flex-direction:column;
          overflow:hidden;
        ">
          {/* Panel header */}
          <div style="padding:14px 16px;border-bottom:1px solid var(--color-border);display:flex;align-items:center;justify-content:space-between;flex-shrink:0">
            <div>
              <div style="font-size:14px;font-weight:700">📚 Handleiding</div>
              {article && (
                <div style="font-size:11px;color:var(--color-text-muted);margin-top:1px">{article.title}</div>
              )}
            </div>
            <button
              onClick={() => { route('/help'); setOpen(false); }}
              style="background:var(--color-bg-secondary);border:none;cursor:pointer;color:var(--color-primary);font-size:12px;font-weight:600;padding:4px 10px;border-radius:6px;white-space:nowrap"
            >
              Alle artikelen →
            </button>
          </div>

          {/* Panel body */}
          <div style="flex:1;overflow-y:auto;padding:16px">
            {loading ? (
              <div style="text-align:center;padding:40px 0;color:var(--color-text-muted);font-size:13px">Laden...</div>
            ) : article ? (
              <div>
                {(() => {
                  const tc = TYPE_COLORS[article.article_type] || TYPE_COLORS.handleiding;
                  return (
                    <span style={`font-size:10px;font-weight:600;padding:2px 8px;border-radius:8px;background:${tc.bg};color:${tc.color};display:inline-block;margin-bottom:10px`}>
                      {article.article_type === 'werkinstructie' ? 'Werkinstructie' : 'Handleiding'}
                    </span>
                  );
                })()}
                <div>{renderMarkdown(article.content)}</div>
              </div>
            ) : (
              <div style="text-align:center;padding:40px 0;color:var(--color-text-muted)">
                <div style="font-size:32px;margin-bottom:10px">📭</div>
                <div style="font-size:13px;font-weight:500;margin-bottom:4px">Geen handleiding beschikbaar</div>
                <div style="font-size:12px">
                  <span
                    onClick={() => { route('/help'); setOpen(false); }}
                    style="color:var(--color-primary);cursor:pointer;text-decoration:underline"
                  >Bekijk alle artikelen</span>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}
